import { useState, useCallback, useEffect } from 'react';
import { useAuthStore } from '../../store/auth-store';
import { supabase } from '../supabase';
import toast from 'react-hot-toast';
import type { Note } from '../../types';

export const useNotes = () => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuthStore();

  const fetchNotes = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Transform the data to match the Note type
      const transformedNotes = data?.map(note => ({
        ...note,
        id: note.id,
        title: note.title,
        content: note.content,
        tags: note.tags || [],
        userId: note.user_id,
        createdAt: new Date(note.created_at),
        updatedAt: new Date(note.updated_at),
        isFavorite: note.is_favorite || false
      })) || [];

      setNotes(transformedNotes);
    } catch (error: any) {
      console.error('Error fetching notes:', error);
      toast.error(error.message || 'Failed to fetch notes');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    fetchNotes(); // Initial fetch

    // Subscribe to realtime changes
    const channel = supabase
      .channel('notes_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notes',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('Received real-time update:', payload);
          
          // Transform the payload data to match Note type
          const transformNote = (note: any): Note => ({
            ...note,
            id: note.id,
            title: note.title,
            content: note.content,
            tags: note.tags || [],
            userId: note.user_id,
            createdAt: new Date(note.created_at),
            updatedAt: new Date(note.updated_at),
            isFavorite: note.is_favorite || false
          });

          if (payload.eventType === 'INSERT') {
            setNotes((current) => [transformNote(payload.new), ...current]);
          } else if (payload.eventType === 'DELETE') {
            setNotes((current) => current.filter((note) => note.id !== payload.old.id));
          } else if (payload.eventType === 'UPDATE') {
            setNotes((current) =>
              current.map((note) =>
                note.id === payload.new.id ? transformNote(payload.new) : note
              )
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchNotes]);

  return {
    notes,
    isLoading,
    fetchNotes,
  };
};